import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { BaseDropdownFilterMenu } from '@/components/uiKit/Dropdown/BaseDropdownFilterMenu';
import { ChevronDown } from 'lucide-react';
import { CalendarIcon } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';
import { useOperatorProficiencyFilters } from '@/hooks/AssetsPreviewPage/OperatorProficiency/useOperatorProficiencyFilters';

interface TimeRangeSelectorProps {
  onFilterChange?: (months: number) => void;
}

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({ onFilterChange }) => {
  const { monthsRangeFilter, handleSingleSelect, filters } = useOperatorProficiencyFilters();

  // Local state for the selected value - this is the source of truth for the UI
  const [selectedValue, setSelectedValue] = useState<number | null>(null);

  // Format label with "Last X month(s)" prefix
  const formatMonthLabel = useCallback((value: number): string => {
    if (value === 1) return 'Last Month';
    return `Last ${value} Months`;
  }, []);

  // Get the default value directly from context filters (source of truth from backend)
  // This avoids the race condition where metadata loads before filters.months_range is set
  const defaultValue = useMemo(() => {
    // First priority: use the context's months_range value (set from backend metadata)
    if (filters.months_range !== undefined) {
      return filters.months_range;
    }
    // Fallback: derive from metadata options if context not yet initialized
    if (!monthsRangeFilter?.options.length) return 1;
    const checkedOpt = monthsRangeFilter.options.find((opt) => opt.checked);
    const value = checkedOpt?.value ?? monthsRangeFilter.options[0]?.value;
    return typeof value === 'number' ? value : parseInt(String(value), 10) || 1;
  }, [filters.months_range, monthsRangeFilter]);

  // Track previous context value to detect stale dropdown callbacks
  const prevContextValueRef = useRef<number | undefined>(filters.months_range);

  // Sync local state with context when context value changes
  // This prevents the dropdown from overwriting the backend value with stale state
  useEffect(() => {
    if (filters.months_range !== undefined && filters.months_range !== selectedValue) {
      setSelectedValue(filters.months_range);
    }
    // Update the ref AFTER the effect (so we can detect changes in handleFilterChange)
    prevContextValueRef.current = filters.months_range;
  }, [filters.months_range]);

  // Effective value: use local selection if set, otherwise use default
  const effectiveValue = selectedValue ?? defaultValue;

  // Compute displayed label
  const displayedLabel = formatMonthLabel(effectiveValue);

  // Current selection state for dropdown
  const currentSelection = useMemo(
    () => ({
      undefined: [String(effectiveValue)],
    }),
    [effectiveValue]
  );

  // Memoize dropdown data from metadata
  const dropdownData = useMemo(() => {
    if (!monthsRangeFilter || monthsRangeFilter.options.length === 0) {
      return [{ menuItems: [] }];
    }

    return [
      {
        menuItems: monthsRangeFilter.options.map((option) => {
          const numValue = typeof option.value === 'number' ? option.value : parseInt(String(option.value), 10);
          return {
            id: String(option.value),
            label: formatMonthLabel(numValue),
          };
        }),
      },
    ];
  }, [monthsRangeFilter, formatMonthLabel]);

  const handleFilterChange = useCallback(
    (incomingFilters: Record<string, string[]>) => {
      const selectedId = incomingFilters.undefined?.[0];
      if (!selectedId) return;

      const monthsValue = parseInt(selectedId, 10);

      // Detect stale dropdown callback: context just changed and dropdown is reporting old value
      // This happens when BaseDropdownFilterMenu fires onFilterChange with its internal state
      // when the defaultSelected prop changes
      if (prevContextValueRef.current !== filters.months_range &&
          monthsValue !== filters.months_range &&
          filters.months_range !== undefined) {
        return;
      }

      // Skip if value hasn't changed
      if (monthsValue === effectiveValue) return;

      // Update local state immediately - this controls the displayed label
      setSelectedValue(monthsValue);

      // Update the context filter
      handleSingleSelect('months_range', monthsValue);

      // Call optional callback
      if (onFilterChange) {
        onFilterChange(monthsValue);
      }
    },
    [effectiveValue, handleSingleSelect, onFilterChange, filters.months_range]
  );

  return (
    <div className="[&_button[data-state=open]]:bg-white [&_button[data-state=open]]:text-gray-900 [&_[role=menuitem]]:text-[11px] [&_[role=menuitem]]:py-0.5 [&_label]:text-[11px] [&_[role=group]]:text-[11px]">
      <BaseDropdownFilterMenu
        data={dropdownData}
        multiSelect={false}
        closeOnSelect={true}
        filterLable={displayedLabel}
        variant="outline"
        size="sm"
        defaultSelected={currentSelection}
        onFilterChange={handleFilterChange}
        showAll={false}
        type="dropdown"
        checkmarkPlacement="right"
        menuLabelRender={() => null}
        menuTrigger={
          <>
            <Icon icon={CalendarIcon} className="h-4 w-4" />
            <span className="text-sm">{displayedLabel}</span>
            <ChevronDown className="h-4 w-4" />
          </>
        }
      />
    </div>
  );
};
